<template>
  <div
    class="ux-content-search"
    @keydown="handleKeydown"
    :class="{ invisible: !type }"
  >
    <div class="ux-content-search-selected-items" v-if="list">
      <div
        v-for="(selectedItem, index) in model"
        :key="index"
        class="ux-content-search-selected-item"
      >
        <slot name="selected">{{ selectedItem.title }}</slot>
      </div>
    </div>
    <search
      v-model="keywords"
      @focus="isFocused = true"
      @blur="handleBlur"
      ref="input"
      :placeholder="placeholderText"
      :loading="searching"
      :debounce="preloadedOptions.length ? 50 : 500"
    />
    <ux-list
      v-if="showOptions && (options.length || (createEnabled && keywords))"
      class="ux-content-search-options"
    >
      <ux-list-item
        v-for="(option, index) in options"
        :key="option._id || index"
        :class="{ active: index === activeIndex }"
        @click="selectItem(option, true)"
        @mouseover="activeIndex = index"
        class="ux-content-search-option"
      >
        <slot name="option">
          <flex-row gap center>
            <flex-cell shrink>
              <div class="face" v-if="isProfile">
                <avatar :id="option._id" />
              </div>
              <div class="face media" v-else-if="isMedia">
                <ux-image :item="option" :width="30" :height="30" crop />
              </div>
              <div class="face" v-else-if="isTag">
                <ux-icon icon="fa-tag" />
              </div>
            </flex-cell>
            <flex-cell>
              <div class="ux-content-search-option-title">
                {{ option.title }}
              </div>
              <div
                class="ux-content-search-option-subtitle"
                v-if="$slots.subtitle || optionSubtitle(option)"
              >
                <slot name="subtitle" :option="option">{{
                  optionSubtitle(option)
                }}</slot>
              </div>
            </flex-cell>
          </flex-row>
        </slot>
      </ux-list-item>
      <ux-list-item
        v-if="createEnabled && keywords"
        :class="{ active: createIndex === activeIndex }"
        @click="createNew"
        @mouseover="activeIndex = createIndex"
        class="ux-content-search-option ux-content-search-create"
      >
        <flex-row gap center>
          <flex-cell shrink>
            <ux-icon icon="fa-plus" />
          </flex-cell>
          <flex-cell> Create new "{{ keywords }}" </flex-cell>
        </flex-row>
      </ux-list-item>
      <ux-list-item
        :class="{ active: browseIndex === activeIndex }"
        @click="browse"
        @mouseover="activeIndex = browseIndex"
        class="ux-content-search-option ux-content-search-browse"
      >
        <flex-row gap center>
          <flex-cell shrink>
            <ux-icon icon="fa-search" />
          </flex-cell>
          <flex-cell>Advanced search</flex-cell>
        </flex-row>
      </ux-list-item>
    </ux-list>
  </div>
</template>

<script>
import Search from "../../form/inputs/search.vue";
import {
  subtitleSelectFields,
  resolveSubtitle,
} from "./contentSearchSubtitles.js";
import { filterExcluded } from "./contentSearchExclude.js";

export default {
  components: {
    Search,
  },
  props: {
    modelValue: {
      type: Array,
      default() {
        return [];
      },
    },
    search: {
      type: String,
      default: "",
    },
    type: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
    },
    multiple: {
      type: Boolean,
      default() {
        return false;
      },
    },
    list: {
      type: Boolean,
      default: true,
    },
    createEnabled: {
      type: Boolean,
      default: false,
    },
    asyncOptions: {
      type: String,
    },
    serverFiltered: {
      type: Boolean,
      default: false,
    },
    preloadedOptions: {
      type: Array,
      default() {
        return [];
      },
    },
    lockFilter: {
      type: Object,
    },
    exclude: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      keywords: this.search,
      options: [],
      model: [],
      activeIndex: -1,
      isFocused: false,
      showOptions: false,
      searching: false,
    };
  },
  created() {
    this.$glossary.ensure();
  },
  computed: {
    glossary() {
      return this.$glossary.hash;
    },
    isProfile() {
      if (this.type === "profile") return true;
      return this.definition?.definesType === "profile";
    },
    isMedia() {
      const definesType = this.definition?.definesType || this.type;
      return definesType === "image" || definesType === "video";
    },
    isTag() {
      return this.type === "tag";
    },
    placeholderText() {
      if (this.multiple) {
        return (
          this.placeholder ||
          `Search to select ${this.definition?.plural || "items"}` ||
          "Search to add items"
        );
      } else {
        return (
          this.placeholder ||
          `Search for an ${this.definition?.title || "item"}` ||
          "Search for an item"
        );
      }
    },
    definition() {
      const match = this.glossary[this.type];
      return match;
    },
    createIndex() {
      return this.options.length;
    },
    browseIndex() {
      return (
        this.options.length + (this.createEnabled && this.keywords ? 1 : 0)
      );
    },
    totalOptions() {
      return this.browseIndex + 1;
    },
  },
  watch: {
    modelValue: {
      handler(m) {
        if (!Array.isArray(m)) {
          if (m._id) {
            m = [m];
          } else {
            m = [];
          }
        }
        this.model = (m || []).slice();
      },
      immediate: true,
      deep: true,
    },
    search(newSearch) {
      this.keywords = newSearch;
    },
    keywords: {
      immediate: true,
      async handler(search) {
        // Emit the search value to parent
        this.$emit("update:search", search);

        this.options = [];
        this.showOptions = false;
        if (!search) {
          this.searching = false;
          return;
        }

        this.searching = true;

        try {
          if (this.preloadedOptions.length) {
            // Filter preloaded options client-side
            const kw = search.toLowerCase();
            this.options = this.preloadedOptions.filter(function (item) {
              return (item.title || "").toLowerCase().includes(kw);
            });
          } else if (this.asyncOptions) {
            // Fetch from the provided URL with search keyword
            const separator = this.asyncOptions.includes("?") ? "&" : "?";
            const url = `${this.asyncOptions}${separator}search=${encodeURIComponent(search)}`;
            const { data } = await this.$sdk.api.get(url);
            let results = (data || []).map(function (item) {
              if (typeof item === "string") {
                return { title: item, _id: item };
              }
              return {
                ...item,
                _id: item._id || item.value || item.title || item.name,
                title:
                  item.title ||
                  item.name ||
                  item.label ||
                  item.value ||
                  String(item),
              };
            });

            // Filter client-side by default unless serverFiltered is true
            if (!this.serverFiltered) {
              const kw = search.toLowerCase();
              results = results.filter(function (item) {
                return item.title.toLowerCase().includes(kw);
              });
            }

            this.options = results;
          } else {
            // Default: search content items via SDK
            if (!this.type) {
              this.searching = false;
              return;
            }
            const criteria = {
              search,
              select: [
                "title",
                "firstName",
                "lastName",
                ...subtitleSelectFields(this.definition, this.type),
              ],
              limit: 5,
            };
            if (this.lockFilter) {
              criteria.filter = this.lockFilter;
            }
            const { items } = await this.$sdk.content.list(this.type, criteria);
            this.options = items;
          }

          this.options = filterExcluded(this.options, this.exclude);
          this.showOptions = true;
          this.activeIndex = this.options.length > 0 ? 0 : -1;
        } catch (err) {
          // Silently handle fetch errors
        } finally {
          this.searching = false;
        }
      },
    },
    options(newOptions) {
      // Reset active index when options change
      this.activeIndex = this.totalOptions > 0 ? 0 : -1;
    },
  },
  methods: {
    optionSubtitle(option) {
      return resolveSubtitle(option, this.definition, this.type, {
        sdk: this.$sdk,
      });
    },
    focus() {
      this.$refs.input.focus();
    },
    dispatch() {
      this.$emit("update:modelValue", this.model);
    },
    handleKeydown(event) {
      if (!this.showOptions || this.totalOptions === 0) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          this.activeIndex = (this.activeIndex + 1) % this.totalOptions;
          this.$nextTick(() => {
            const activeElement = this.$el.querySelector(
              ".ux-content-search-option.active",
            );
            if (activeElement) {
              activeElement.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
              });
            }
          });
          break;
        case "ArrowUp":
          event.preventDefault();
          this.activeIndex =
            this.activeIndex <= 0
              ? this.totalOptions - 1
              : this.activeIndex - 1;
          this.$nextTick(() => {
            const activeElement = this.$el.querySelector(
              ".ux-content-search-option.active",
            );
            if (activeElement) {
              activeElement.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
              });
            }
          });
          break;
        case "Enter":
          event.preventDefault();
          if (this.activeIndex >= 0 && this.activeIndex < this.options.length) {
            this.selectItem(this.options[this.activeIndex], true);
          } else if (
            this.activeIndex === this.createIndex &&
            this.createEnabled &&
            this.keywords
          ) {
            this.createNew();
          } else if (this.activeIndex === this.browseIndex) {
            this.browse();
          }
          break;
        case "Escape":
          this.showOptions = false;
          this.$refs.input?.$el?.blur();
          break;
      }
    },
    selectItem(option, shouldRefocus = false) {
      // Add the selected item to the model if it's not already there
      const isAlreadySelected = this.model.some(
        (item) => item._id === option._id,
      );

      if (!isAlreadySelected) {
        // If it's only single
        if (!this.multiple) {
          // Wipe the list
          this.model = [option];
        } else {
          this.model.push(option);
        }
        this.dispatch();
      }

      // Clear the search and options
      this.keywords = "";
      this.options = [];
      this.showOptions = false;

      // Focus back on the search input for better UX if requested
      if (shouldRefocus) {
        this.$nextTick(() => {
          this.$refs.input?.$el?.focus();
        });
      }
    },
    browse() {
      this.keywords = "";
      this.options = [];
      this.showOptions = false;
      this.$emit("browse");
    },
    createNew() {
      // Emit create event with current keywords
      this.$emit("create", this.keywords);

      // Clear the search and options
      this.keywords = "";
      this.options = [];
      this.showOptions = false;

      // Refocus on the search input
      this.$nextTick(() => {
        this.$refs.input?.$el?.focus();
      });
    },
    handleBlur() {
      // Delay hiding options to allow click events to register
      setTimeout(() => {
        this.isFocused = false;
        this.showOptions = false;
      }, 200);
    },
  },
  mounted() {
    this.handleClickOutside = (event) => {
      if (this.$el && !this.$el.contains(event.target)) {
        this.showOptions = false;
      }
    };
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
};
</script>

<style lang="scss" scoped>
.invisible {
  opacity: 0.5;
  pointer-events: none;
}
.ux-content-search {
  position: relative;
}

.face {
  width: 20px;

  &.media {
    width: 30px;
    height: 30px;
    border-radius: 3px;
    overflow: hidden;
  }
}

.ux-content-search-selected-items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.ux-content-search-selected-item {
  background-color: #f0f0f0;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;
}

.ux-content-search-options {
  position: absolute;
  max-height: 20vh;
  overflow-y: auto;
  z-index: 100;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid color-mix(in lab, currentColor, var(--bg1));
  border-radius: 4px;
  margin-top: 4px;
  background-color: var(--bg1);
}

.ux-content-search-option.active {
  background-color: color-mix(in lab, var(--primary) 15%, var(--bg1));
}

.ux-content-search-option-subtitle {
  font-size: 0.8em;
  opacity: 0.6;
  margin-top: 0.1em;
}

.ux-content-search-create {
  border-top: 1px solid color-mix(in lab, currentColor 10%, var(--bg1));
  font-style: italic;
  color: color-mix(in lab, var(--primary), currentColor 70%);
}

.ux-content-search-browse {
  border-top: 1px solid color-mix(in lab, currentColor 10%, var(--bg1));
  font-style: italic;
  color: color-mix(in lab, var(--primary), currentColor 70%);
}

.ux-content-search-create .face,
.ux-content-search-browse .face {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
